/* Noticing that the page itself has gone out of date.
 *
 * A single-page app keeps running the JavaScript it was loaded with. Once a
 * deploy replaces `web/dist`, an open tab goes on running the previous build,
 * and the hashed bundles it would lazily fetch are no longer on disk to fetch —
 * so the failure people actually hit is a button that silently does nothing,
 * long after the deploy they never saw. The server publishes an identity for
 * the build it is serving; this watches it and says when a reload would give
 * the reader something different.
 */

/** How often an open tab asks. Slow on purpose: this is a courtesy, not a
 *  health check, and the visibility check below covers the case that matters —
 *  a tab left open for days and come back to. */
export const VERSION_POLL_MS = 60_000;

export interface VersionResponse {
  /** `null` where the server has no built page to fingerprint. */
  build: string | null;
}

/** The build the server is serving now, or `null` for "could not tell".
 *
 *  Every failure is `null` rather than a throw. A version check is the last
 *  thing that should put an error in front of someone: if it cannot answer, the
 *  page carries on exactly as it would have without it.
 */
export async function fetchBuild(signal?: AbortSignal): Promise<string | null> {
  try {
    const response = await fetch("/api/portal/v1/version", {
      credentials: "include",
      // The one request on the page that must never be served from cache: a
      // cached version check cannot see a new version.
      cache: "no-store",
      signal,
    });
    if (!response.ok) return null;
    const body = (await response.json()) as VersionResponse;
    return body.build ?? null;
  } catch {
    return null;
  }
}

/** Whether the running page is behind the server.
 *
 *  An unknown build on either side is not an update. Treating "cannot tell" as
 *  "out of date" would put a reload prompt in front of every reader the moment
 *  the endpoint had a bad minute.
 */
export function isOutdated(loaded: string | null, current: string | null): boolean {
  if (loaded === null || current === null) return false;
  return loaded !== current;
}
